import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useIsAdmin } from "~/entities/admin";
import { MessageSelectionProvider, MoveSelectionBar } from "~/features/move-messages";
import { ChatDropOverlay } from "~/features/upload-attachment/ChatDropOverlay";
import { useChatUIStore } from "~/shared/store/useChatUIStore";
import { PromptBox } from "~/widgets/chat-prompt/PromptBox";
import { VirtualMessageList } from "~/widgets/chat-messages/VirtualMessageList";

import { ChatArtifactGallery } from "./chat-artifact-gallery";
import { ChatHeader } from "./ChatHeader";
import { useChatLayout } from "~/shared/context/ChatLayoutContext";
import { ChatShellLayout } from "./ChatShellLayout";
import { useChatGenerationFlow } from "./useChatGenerationFlow";
import { useChatRouteGuards } from "./useChatRouteGuards";
import { useChatShellContext } from "./useChatShellContext";
import { useChatShellDropZone } from "./useChatShellDropZone";

export function ChatShell() {
    const { t } = useTranslation("chat");
    const { chatId } = useParams<{ chatId: string }>();
    const { heroMedia } = useChatLayout();

    // VirtualMessageList публикует свой handleNavScroll сюда; ChatHeader
    // (in-chat search) использует тот же ref для скролла к найденному
    // сообщению — auto-expand свёрнутых групп получаем «бесплатно».
    const scrollToMessageRef = useRef<((messageId: string) => void) | null>(null);

    useChatRouteGuards(chatId);
    // Только суперадмин может выбирать и перемещать импортированные
    // сообщения между чатами — под ним монтируем провайдер выбора + панель.
    const isSuperAdmin = useIsAdmin().data ?? false;
    const { headerProps, canWrite } = useChatShellContext(chatId);
    const {
        messages,
        hasMessages,
        isMessagesLoading,
        hasOlderMessages,
        isLoadingOlderMessages,
        loadOlderMessages,
        jobs,
        retryingMessageId,
        handleSubmit,
        handleRetry,
    } = useChatGenerationFlow(chatId);
    const { isDragOver, handlers: dropHandlers } = useChatShellDropZone(chatId);

    const galleryTarget = useChatUIStore((s) => s.galleryTarget);
    const closeGallery = useChatUIStore((s) => s.closeGallery);
    // При переходе в другой чат закрываем галерею — иначе цель из прошлого
    // чата «протекла» бы в новый (артефакты там другие, якорь не нашёлся бы).
    useEffect(() => {
        closeGallery();
    }, [chatId, closeGallery]);

    // relative + dropHandlers — drop-zone покрывает весь main-column (messages +
    // prompt). Overlay рендерится поверх через absolute inset-0;
    // pointer-events-none у overlay чтобы не сбивать dragenter/leave-counter в хуке.
    //
    // ВАЖНО: класс `drop-zone` обязателен — глобальный legacy-хук
    // `src/hooks/useDragDropPrevention` (подключён в App.tsx для защиты от drop'а
    // файлов мимо drop-зон) на window-level перебивает
    // `dataTransfer.dropEffect = "none"` для всего что не имеет этот класс через
    // `closest()`. Без него браузер видит `dropEffect=none` и НЕ генерирует drop
    // event, наш onDrop никогда не вызывается. См. feedback_legacy_drag_drop_prevention.
    const mainColumn = (
        <div
            className="drop-zone relative flex min-h-0 flex-1 flex-col"
            onDragEnter={dropHandlers.onDragEnter}
            onDragOver={dropHandlers.onDragOver}
            onDragLeave={dropHandlers.onDragLeave}
            onDrop={dropHandlers.onDrop}
        >
            <MoveSelectionBar chatId={chatId} />
            {hasMessages ? (
                <VirtualMessageList
                    messages={messages}
                    heroMedia={heroMedia}
                    jobsByMessageId={jobs}
                    onRetry={handleRetry}
                    retryingMessageId={retryingMessageId}
                    scrollToMessageRef={scrollToMessageRef}
                    hasOlderMessages={hasOlderMessages}
                    isLoadingOlderMessages={isLoadingOlderMessages}
                    onLoadOlderMessages={loadOlderMessages}
                />
            ) : isMessagesLoading ? (
                <div className="min-h-0 flex-1" />
            ) : (
                <div className="flex min-h-0 flex-1 items-center justify-center px-6 text-center text-sm text-zinc-500">
                    {t("emptyChat.hint")}
                </div>
            )}
            <PromptBox chatId={chatId ?? null} onSubmit={handleSubmit} disabled={!canWrite} />
            <ChatDropOverlay isDragOver={isDragOver} />
            {chatId && <ChatArtifactGallery chatId={chatId} target={galleryTarget} onClose={closeGallery} />}
        </div>
    );

    return (
        <ChatShellLayout
            header={<ChatHeader {...headerProps} scrollToMessageRef={scrollToMessageRef} />}
            overlays={null}
            main={
                // Провайдер выбора монтируется только суперадмину — иначе чек-боксы
                // и панель переноса недоступны (MoveSelectionBar без выбора = null).
                isSuperAdmin ? (
                    <MessageSelectionProvider chatId={chatId}>{mainColumn}</MessageSelectionProvider>
                ) : (
                    mainColumn
                )
            }
        />
    );
}
